import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Input, Row } from "antd";
import ActionModal from "components/Settings/components/ActionModal";
import { retrieveToken, storeToken } from "utils/storage";
import { useSelectCode } from "hooks/api/useListing";
import Select from "components/BaseWidgets/select";

function UpdateModal({ open, setOpen, update }) {
  const [form] = Form.useForm();
  const [phoneLength, setPhoneLength] = useState(10);
  const updatedMail = retrieveToken("updatedMail");
  const updatedMobile = retrieveToken("updatedMobile");
  const updatedCountryCode = retrieveToken("updatedCountryCode");

  const { data: countrylist } = useSelectCode({ search_key: "" });

  const CountryCodes = useMemo(() => {
    return (
      countrylist?.country_codes?.map((item) => ({
        value: item.id,
        label: item.code,
        phone_length: item.phone_length,
      })) || []
    );
  }, [countrylist]);

  const getDefaultCountry = () => {
    return (
      CountryCodes.find((c) => c.value === updatedCountryCode) ||
      CountryCodes.find((c) => c.label === "+91") ||
      CountryCodes[0] ||
      null
    );
  };

  useEffect(() => {
    if (open && CountryCodes.length) {
      const defaultCountry = getDefaultCountry();
      form.setFieldsValue({
        email: updatedMail || "",
        phone: updatedMobile || "",

        country_code: defaultCountry?.value,
      });
      setPhoneLength(defaultCountry?.phone_length || 10);
    }
  }, [open, CountryCodes]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const payload = form.getFieldsValue();
      if (update === "mail") {
        storeToken("updatedMail", form.getFieldValue("email"));
      } else {
        storeToken("updatedMobile", form.getFieldValue("phone"));
        storeToken("updatedCountryCode", form.getFieldValue("country_code"));
      }
      setOpen(false);
      form.resetFields();
    } catch (err) {
    }
  };

  const handleCountryChange = (value) => {
    const selectedCountry = CountryCodes.find((c) => c.value === value);
    setPhoneLength(selectedCountry?.phone_length || 10);
    form.setFieldValue("phone", "");
  };

  return (
    <ActionModal
      title={
        update === "mail" ? "Change Email Address" : "Change Mobile Number"
      }
      description={
        update === "mail"
          ? "Enter your new email to update your account. You may need to verify the new address to complete the change."
          : "Enter your number to update your account. You may need to verify the number to complete the change."
      }
      open={open}
      setOpen={setOpen}
      footer={{
        left: { label: "Cancel", action: () => setOpen(false) },
        right: {
          label: update === "mail" ? "Update email" : "Update number",
          action: handleSubmit,
        },
      }}
    >
      <Form form={form} layout="vertical" className="w-full">
        {update === "mail" ? (
          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
            className="mb-0"
          >
            <Input placeholder="Enter your new email ID" />
          </Form.Item>
        ) : (
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item
                name="country_code"
                rules={[
                  { required: true, message: "Please select a country code" },
                ]}
                className="mb-0"
              >
                <Select
                  // showSearch={false}
                  placeholder="Code"
                  options={CountryCodes}
                  onChange={handleCountryChange}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="phone"
                rules={[
                  { required: true, message: "Please enter your number" },
                  {
                    pattern: /^[0-9]{7,15}$/,
                    message: "Enter a valid phone number",
                  },
                  {
                    validator: (_, value) => {
                      const selectedCountry = CountryCodes.find(
                        (c) => c.value === form.getFieldValue("country_code"),
                      );

                      if (
                        selectedCountry?.phone_length &&
                        value?.length !== selectedCountry.phone_length
                      ) {
                        return Promise.reject(
                          new Error(
                            `Phone number must be exactly ${selectedCountry.phone_length} digits`,
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                className="mb-0"
              >
                <Input
                  placeholder="Enter your new number"
                  maxLength={phoneLength}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </ActionModal>
  );
}

export default UpdateModal;
